import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import {
  ArrowRight, ArrowUpRight, GraduationCap, Layers, Video, Cpu, ClipboardCheck,
  Compass, TrendingUp, Heart, Award, ShieldCheck, Lightbulb, Globe2, KeyRound,
  Palette, BookOpen, Newspaper, Accessibility, Gem, UserCheck, Wrench, DoorOpen,
} from "lucide-react";
import { api, fileUrl } from "@/api";
import Reveal from "@/components/Reveal";
import Section from "@/components/Section";
import Portrait from "@/components/Portrait";
import Empty from "@/components/Empty";

const PILLARS = [
  { icon: Accessibility, title: "Accessibility", text: "Make high-quality education accessible irrespective of geographic location." },
  { icon: Gem, title: "Quality", text: "Develop structured programmes supported by knowledgeable educators." },
  { icon: UserCheck, title: "Personalisation", text: "Recognise that every learner has different goals and learning speeds." },
  { icon: Wrench, title: "Technology", text: "Use technology to enhance — not replace — the relationship between educators and learners." },
  { icon: DoorOpen, title: "Opportunity", text: "Help learners develop capabilities that create academic, professional and personal opportunities." },
];

const VALUES = [
  { icon: Heart, title: "Learner First", text: "Every product and decision should ultimately improve the learner experience." },
  { icon: Award, title: "Excellence", text: "Maintain high standards in teaching, curriculum, technology and operations." },
  { icon: ShieldCheck, title: "Integrity", text: "Operate transparently and responsibly with learners, educators, employees and partners." },
  { icon: Lightbulb, title: "Innovation", text: "Continuously explore better ways of teaching and learning." },
  { icon: Globe2, title: "Inclusivity", text: "Make meaningful learning opportunities available across geographical and socioeconomic boundaries." },
  { icon: KeyRound, title: "Ownership", text: "Encourage accountability and entrepreneurial thinking throughout the organisation." },
  { icon: Palette, title: "Creativity", text: "Recognise creative thinking as an essential component of both education and business." },
  { icon: BookOpen, title: "Continuous Learning", text: "Remain an organisation that learns while helping others learn." },
];

const APPROACH = [
  { icon: GraduationCap, label: "Expert Educators" },
  { icon: Layers, label: "Structured Curriculum" },
  { icon: Video, label: "Live Learning" },
  { icon: Cpu, label: "Technology" },
  { icon: ClipboardCheck, label: "Assessment" },
  { icon: Compass, label: "Personalised Guidance" },
  { icon: TrendingUp, label: "Learner Progress" },
];

const CAPABILITIES = [
  "Learning Management Systems", "Student Dashboards", "Teacher Dashboards",
  "Digital Learning Resources", "Live Online Classrooms", "Assessments",
  "Progress Tracking", "Curriculum Management", "Digital Payments",
  "Analytics", "Automated Communication", "Learner Engagement Systems",
];

function Ecosystem({ brands }) {
  const n = Math.max(brands.length, 1);
  const xs = brands.map((_, i) => (800 / (n + 1)) * (i + 1));
  return (
    <div data-testid="brand-ecosystem" className="mx-auto max-w-4xl">
      <svg viewBox="0 0 800 320" className="w-full" role="img" aria-label="RCS brand ecosystem diagram">
        {xs.map((x, i) => (
          <motion.path
            key={brands[i].id}
            d={`M 400 92 C 400 160, ${x} 150, ${x} 218`}
            fill="none"
            stroke="#D4AF37"
            strokeWidth="1.5"
            strokeDasharray="4 5"
            initial={{ pathLength: 0, opacity: 0 }}
            whileInView={{ pathLength: 1, opacity: 0.8 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, delay: 0.3 + i * 0.25 }}
          />
        ))}
        <g>
          <rect x="250" y="24" width="300" height="68" rx="10" fill="#1C2541" stroke="#D4AF37" strokeWidth="1.2" />
          <text x="400" y="54" textAnchor="middle" fill="#FAF8F5" fontFamily="Playfair Display, serif" fontSize="19" fontWeight="600">
            Roy Career Solutions
          </text>
          <text x="400" y="76" textAnchor="middle" fill="#D4AF37" fontSize="10" letterSpacing="3">
            PARENT COMPANY
          </text>
        </g>
        {brands.map((b, i) => (
          <motion.g key={b.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.8 + i * 0.25 }}>
            <rect x={xs[i] - 130} y="218" width="260" height="76" rx="10" fill="#0B132B" stroke={b.brandColor || "#D4AF37"} strokeWidth="1.2" />
            <text x={xs[i]} y="250" textAnchor="middle" fill="#FAF8F5" fontFamily="Playfair Display, serif" fontSize="15" fontWeight="600">
              {b.brandName}
            </text>
            <text x={xs[i]} y="272" textAnchor="middle" fill={b.brandColor || "#D4AF37"} fontSize="10" letterSpacing="2">
              {(b.category || "").toUpperCase()}
            </text>
          </motion.g>
        ))}
      </svg>
    </div>
  );
}

const GRID_BG = {
  backgroundImage:
    "linear-gradient(rgba(212,175,55,0.35) 1px, transparent 1px), linear-gradient(90deg, rgba(212,175,55,0.35) 1px, transparent 1px)",
  backgroundSize: "72px 72px",
};

const HERO_VISUAL =
  "https://static.prod-images.emergentagent.com/jobs/809f791b-b844-4160-8681-dfdbe4e5b2ac/images/859960c8d193eb46e985cf453a50550a5844141113d66a526f75654c2650ba7a.jpeg";

const HERO_CHIPS = [
  { label: "Academics", cls: "-left-4 top-14 lg:-left-10", duration: 5.5 },
  { label: "Music", cls: "-right-2 top-1/3 lg:-right-6", duration: 6.5 },
  { label: "Technology", cls: "left-6 -bottom-2 lg:left-2", duration: 7.2 },
];

function HeroVisual() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 16 });
  const sy = useSpring(my, { stiffness: 55, damping: 16 });
  const rotateX = useTransform(sy, [-0.5, 0.5], [9, -9]);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-9, 9]);

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="relative flex items-center justify-center lg:col-span-5"
      style={{ perspective: 900 }}
      data-testid="hero-visual"
    >
      <motion.div
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        className="relative w-full max-w-xs sm:max-w-sm lg:max-w-lg"
      >
        <motion.img
          src={HERO_VISUAL}
          alt="The RCS learning ecosystem — an open glowing book, graduation cap and flowing music notation connected by golden constellation lines"
          initial={{ opacity: 0, scale: 0.9, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="pointer-events-none w-full select-none mix-blend-screen"
          style={{
            filter: "drop-shadow(0 0 70px rgba(212,175,55,0.22))",
            WebkitMaskImage: "radial-gradient(ellipse at center, black 52%, transparent 76%)",
            maskImage: "radial-gradient(ellipse at center, black 52%, transparent 76%)",
          }}
          data-testid="hero-visual-image"
        />
        {HERO_CHIPS.map((c, i) => (
          <motion.span
            key={c.label}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1, y: [0, -10, 0] }}
            transition={{
              opacity: { duration: 0.6, delay: 1 + i * 0.25 },
              scale: { duration: 0.6, delay: 1 + i * 0.25 },
              y: { duration: c.duration, repeat: Infinity, ease: "easeInOut", delay: i * 0.6 },
            }}
            className={`absolute ${c.cls} rounded-full border border-gold/40 bg-navy-950/70 px-4 py-1.5 text-[10px] font-mono font-semibold uppercase tracking-[0.25em] text-gold backdrop-blur-md`}
            data-testid={`hero-chip-${c.label.toLowerCase()}`}
          >
            {c.label}
          </motion.span>
        ))}
      </motion.div>
    </div>
  );
}

export default function Home() {
  const [company, setCompany] = useState(null);
  const [settings, setSettings] = useState(null);
  const [brands, setBrands] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [articles, setArticles] = useState([]);

  useEffect(() => {
    api.get("/company").then((r) => setCompany(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    api.get("/leadership").then((r) => setLeaders(r.data.filter((l) => l.featured))).catch(() => {});
    api.get("/milestones").then((r) => setMilestones(r.data)).catch(() => {});
    api.get("/articles").then((r) => setArticles(r.data.slice(0, 3))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative flex min-h-[100svh] items-center overflow-hidden bg-navy-950 pt-24" data-testid="hero-section">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.12]"
          style={GRID_BG}
        />
        <div aria-hidden className="pointer-events-none absolute -top-40 left-1/3 h-[480px] w-[480px] rounded-full bg-gold/10 blur-3xl animate-slow-pulse" />
        <div aria-hidden className="pointer-events-none absolute bottom-0 right-0 h-96 w-96 rounded-full bg-navy-800/80 blur-3xl" />

        <div className="container-rcs relative grid items-center gap-16 py-24 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <p className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/5 px-4 py-1.5 text-[11px] font-mono uppercase tracking-[0.25em] text-gold" data-testid="hero-cin-badge">
                CIN {company?.cin || "U85500KA2024PTC194012"} · Est. 2024 · Bengaluru
              </p>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="mt-8 font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight text-ivory"
              data-testid="hero-heading"
            >
              Building the Future <br className="hidden sm:block" />of <span className="italic text-gold">Learning.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.3 }}
              className="mt-8 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-300"
              data-testid="hero-subtext"
            >
              Roy Career Solutions Private Limited builds innovative education brands designed to make
              academic and creative learning accessible, structured, engaging and impactful.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.45 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link to="/about" className="btn-gold" data-testid="hero-discover-rcs">
                Discover RCS <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
              <Link to="/brands" className="btn-outline-light" data-testid="hero-explore-brands">
                Explore Our Brands <ArrowUpRight className="h-4 w-4" aria-hidden />
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.7 }}
              className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-white/10 pt-8"
              data-testid="hero-brand-strip"
            >
              <span className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Our Brands</span>
              {brands.map((b) => (
                <a
                  key={b.id}
                  href={b.websiteURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`hero-brand-${b.slug}`}
                  className="group inline-flex items-center gap-2 text-sm font-semibold text-slate-300 transition-colors hover:text-gold"
                >
                  <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: b.brandColor || "#D4AF37" }} />
                  {b.brandName}
                  <ArrowUpRight className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" aria-hidden />
                </a>
              ))}
            </motion.div>
          </div>
          <HeroVisual />
        </div>
      </section>

      {/* WHO WE ARE */}
      <section className="py-24 lg:py-32" data-testid="who-we-are">
        <div className="container-rcs grid items-center gap-16 lg:grid-cols-2">
          <div>
            <Section
              eyebrow="Who We Are"
              title="An education company built for the next generation of learners."
            />
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-slate-600" data-testid="who-we-are-copy">
                {company?.about ||
                  "Roy Career Solutions Private Limited is an education-focused organisation established with the vision of creating meaningful, technology-enabled learning experiences."}
              </p>
              <div className="mt-8 flex flex-wrap gap-2.5" data-testid="who-we-are-traits">
                {["Expert Educators", "Structured Curricula", "Technology", "Learner-Centric", "Personalised Learning", "Digital Accessibility", "Operational Excellence"].map((t) => (
                  <span key={t} className="rounded-full border border-navy-900/15 bg-white px-4 py-1.5 text-xs font-semibold text-navy-900">
                    {t}
                  </span>
                ))}
              </div>
              <Link to="/about" className="link-underline mt-10 inline-flex" data-testid="who-we-are-cta">
                Know More About RCS <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="relative">
            <div className="overflow-hidden rounded-2xl">
              <img
                src="https://images.unsplash.com/photo-1577962917302-cd874c4e31d2?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Leadership team collaborating at Roy Career Solutions"
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
                data-testid="who-we-are-image"
              />
            </div>
            <div className="absolute -bottom-8 -left-4 sm:-left-8 rounded-2xl border border-gold/30 bg-navy-950 p-6 shadow-2xl">
              <p className="eyebrow-light">Incorporated</p>
              <p className="mt-2 font-serif text-2xl text-ivory" data-testid="incorporation-card">{company?.incorporationDate || "16 October 2024"}</p>
              <p className="mt-1 text-xs text-slate-400">{company?.corporateLocation || "Bengaluru, Karnataka, India"}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* COMPANY AT A GLANCE */}
      <section className="bg-navy-950 py-20 lg:py-24" data-testid="company-at-a-glance">
        <div className="container-rcs">
          <Section dark eyebrow="Company at a Glance" title="The essentials, at a glance." />
          <div className="mt-14 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 md:grid-cols-3 lg:grid-cols-5">
            {(settings?.stats || []).map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08} className="bg-navy-950">
                <div className="flex h-full flex-col justify-between gap-6 p-7" data-testid={`stat-${i}`}>
                  <span className="font-serif text-3xl lg:text-4xl text-gold">{s.value}</span>
                  <span className="text-xs uppercase tracking-[0.2em] text-slate-400">{s.label}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OUR BRANDS */}
      <section className="py-24 lg:py-32" data-testid="our-brands">
        <div className="container-rcs">
          <Section
            eyebrow="Our Brands"
            title="Different learning journeys. One shared commitment to excellence."
            lede="Each RCS brand is a specialised learning organisation — built with its own educators, curricula and technology, united by a single vision for education."
          />
          <div className="mt-14 grid gap-8 lg:grid-cols-2">
            {brands.map((b, i) => (
              <Reveal key={b.id} delay={i * 0.12}>
                <article
                  className="group relative overflow-hidden rounded-2xl border border-navy-900/10 bg-white transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_28px_60px_-24px_rgba(11,19,43,0.35)]"
                  data-testid={`brand-tile-${b.slug}`}
                >
                  <div className="relative h-60 overflow-hidden sm:h-72">
                    <img
                      src={fileUrl(b.heroImage)}
                      alt={`${b.brandName} — ${b.category}`}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-navy-950/35" />
                    <span
                      className="absolute left-6 top-6 rounded-full px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white"
                      style={{ backgroundColor: b.brandColor || "#D4AF37" }}
                    >
                      {b.category}
                    </span>
                  </div>
                  <div className="p-8">
                    <h3 className="font-serif text-2xl text-navy-900 sm:text-3xl">{b.brandName}</h3>
                    <p className="mt-4 text-sm leading-relaxed text-slate-600">{b.description}</p>
                    <div className="mt-8 flex flex-wrap items-center gap-4">
                      <a
                        href={b.websiteURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`brand-visit-${b.slug}`}
                        className="inline-flex items-center gap-2 rounded-full bg-navy-950 px-6 py-2.5 text-xs font-semibold text-ivory transition-colors hover:bg-gold hover:text-navy-950"
                      >
                        Visit {b.brandName} <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
                      </a>
                      <Link to={`/brands/${b.slug}`} className="link-underline text-sm" data-testid={`brand-overview-${b.slug}`}>
                        Brand Overview <ArrowRight className="h-4 w-4" aria-hidden />
                      </Link>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ECOSYSTEM */}
      <section className="bg-navy-950 py-24 lg:py-32" data-testid="ecosystem-section">
        <div className="container-rcs">
          <Section
            dark
            eyebrow="The RCS Learning Ecosystem"
            title="One company. Multiple learning experiences. One vision for education."
            lede="RCS operates as the parent organisation — building, governing and scaling specialised education brands, with room to grow into new learning verticals over time."
            className="mx-auto text-center [&>p]:mx-auto"
          />
          <div className="mt-16">
            <Ecosystem brands={brands} />
          </div>
        </div>
      </section>

      {/* WHY WE EXIST */}
      <section className="py-24 lg:py-32" data-testid="why-we-exist">
        <div className="container-rcs">
          <Section
            eyebrow="Why We Exist"
            title="Transforming education through specialised, human-led learning organisations."
            lede="RCS exists to combine the accessibility of digital technology with the effectiveness of structured, human-led teaching."
          />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="card-premium h-full" data-testid={`pillar-${p.title.toLowerCase()}`}>
                  <p.icon className="h-6 w-6 text-gold-dark" aria-hidden />
                  <h3 className="mt-5 font-serif text-xl text-navy-900">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VISION & MISSION */}
      <section className="border-y border-navy-900/10 bg-white py-24 lg:py-32" data-testid="vision-mission">
        <div className="container-rcs grid gap-8 lg:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-2xl bg-navy-950 p-10 lg:p-12" data-testid="vision-card">
              <p className="eyebrow-light">Our Vision</p>
              <p className="mt-6 font-serif text-2xl leading-snug text-ivory sm:text-3xl">
                “{company?.vision || "To build a diverse ecosystem of trusted education brands that empower learners to realise their academic, creative and professional potential."}”
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="flex h-full flex-col rounded-2xl border border-navy-900/10 bg-ivory p-10 lg:p-12" data-testid="mission-card">
              <p className="eyebrow">Our Mission</p>
              <p className="mt-6 text-base leading-relaxed text-slate-700 sm:text-lg">
                {company?.mission ||
                  "To develop learner-centric educational platforms that combine structured curricula, excellent educators, digital accessibility, innovation and personalised learning experiences."}
              </p>
              <div className="mt-auto grid grid-cols-2 gap-3 pt-10">
                {["Quality Education", "Digital Accessibility", "Learner Success", "Continuous Innovation"].map((m) => (
                  <div key={m} className="rounded-xl border border-gold/40 bg-white px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-navy-900" data-testid={`mission-pillar-${m.toLowerCase().replace(/ /g, "-")}`}>
                    {m}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="py-24 lg:py-32" data-testid="core-values">
        <div className="container-rcs">
          <Section eyebrow="Core Values" title="The principles behind every decision we make." />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06}>
                <div className="card-premium group h-full" data-testid={`value-${v.title.toLowerCase().replace(/ /g, "-")}`}>
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy-950 text-gold transition-colors duration-300 group-hover:bg-gold group-hover:text-navy-950">
                    <v.icon className="h-5 w-5" aria-hidden />
                  </div>
                  <h3 className="mt-5 font-serif text-lg text-navy-900">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* OUR APPROACH */}
      <section className="bg-navy-950 py-24 lg:py-32" data-testid="our-approach">
        <div className="container-rcs">
          <Section
            dark
            eyebrow="Our Approach to Education"
            title="Technology that strengthens human-led teaching."
            lede="RCS believes technology should strengthen human-led education — never reduce learning to prerecorded content."
          />
          <div className="mt-16 grid gap-4 sm:grid-cols-2 lg:grid-cols-7">
            {APPROACH.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08}>
                <div className="relative flex h-full flex-col items-center rounded-2xl border border-white/10 bg-navy-900/60 p-6 text-center" data-testid={`approach-${i}`}>
                  <span className="absolute right-4 top-4 font-mono text-[10px] text-gold/60">{String(i + 1).padStart(2, "0")}</span>
                  <s.icon className="h-6 w-6 text-gold" aria-hidden />
                  <span className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-200">{s.label}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LEADERSHIP PREVIEW */}
      <section className="py-24 lg:py-32" data-testid="leadership-preview">
        <div className="container-rcs">
          <Section
            eyebrow="Leadership"
            title="The people shaping our vision and building our businesses."
          />
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {leaders.map((l, i) => (
              <Reveal key={l.id} delay={i * 0.1}>
                <Link
                  to={`/leadership/${l.slug}`}
                  data-testid={`leader-card-${l.slug}`}
                  className="card-premium group flex h-full flex-col items-start"
                >
                  <Portrait name={l.fullName} src={l.photograph} size="md" testid={`leader-portrait-${l.slug}`} />
                  <h3 className="mt-6 font-serif text-2xl text-navy-900 transition-colors group-hover:text-gold-dark">{l.fullName}</h3>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-gold-dark">{l.designation}</p>
                  <p className="mt-1 text-xs text-slate-500">{l.organisation}</p>
                  <span className="link-underline mt-6 text-sm">
                    View Profile <ArrowRight className="h-4 w-4" aria-hidden />
                  </span>
                </Link>
              </Reveal>
            ))}
            <Reveal delay={leaders.length * 0.1}>
              <Link
                to="/leadership"
                data-testid="meet-leadership-cta"
                className="flex h-full min-h-[280px] flex-col items-start justify-between rounded-2xl bg-navy-950 p-8 transition-transform duration-300 hover:-translate-y-1"
              >
                <p className="font-serif text-2xl leading-snug text-ivory">
                  Meet the leadership building our education ecosystem.
                </p>
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-gold">
                  Meet Our Leadership <ArrowRight className="h-4 w-4" aria-hidden />
                </span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* JOURNEY PREVIEW */}
      <section className="border-y border-navy-900/10 bg-white py-24 lg:py-32" data-testid="journey-preview">
        <div className="container-rcs grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Section
              eyebrow="Our Journey"
              title="A young company with a long-term vision."
              lede="Incorporated in October 2024, RCS is building the foundations of a multi-brand education organisation."
            />
            <Reveal delay={0.15}>
              <Link to="/journey" className="link-underline mt-8 inline-flex" data-testid="journey-preview-cta">
                Explore Our Journey <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <ol className="relative space-y-10 border-l border-gold/40 pl-10">
              {milestones.slice(0, 4).map((m, i) => (
                <Reveal key={m.id} delay={i * 0.1}>
                  <li className="relative" data-testid={`milestone-${i}`}>
                    <span className="absolute -left-[47px] top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full border-2 border-gold bg-white" />
                    <span className="font-mono text-xs tracking-[0.2em] text-gold-dark">{m.year}</span>
                    <h3 className="mt-2 font-serif text-xl text-navy-900">{m.title}</h3>
                    <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-600">{m.description}</p>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* INNOVATION */}
      <section className="py-24 lg:py-32" data-testid="innovation-section">
        <div className="container-rcs grid items-start gap-16 lg:grid-cols-2">
          <div>
            <Section
              eyebrow="Technology & Innovation"
              title="Technology that enables better learning."
              lede="Our digital learning ecosystem is being developed around capabilities designed to support educators, learners and academic operations end to end."
            />
            <Reveal delay={0.15}>
              <Link to="/innovation" className="link-underline mt-8 inline-flex" data-testid="innovation-cta">
                Explore Innovation at RCS <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {CAPABILITIES.map((c, i) => (
              <Reveal key={c} delay={i * 0.04}>
                <div className="rounded-xl border border-navy-900/10 bg-white px-4 py-4 text-center text-xs font-semibold text-navy-900" data-testid={`capability-${i}`}>
                  {c}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* IMPACT */}
      <section className="bg-navy-950 py-24 lg:py-28" data-testid="impact-section">
        <div className="container-rcs grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <Section
              dark
              eyebrow="Impact"
              title="Impact, measured honestly."
              lede="As a young organisation, we publish impact metrics only when they are real and verifiable. Our impact story begins with every learner our brands serve — and grows from there."
            />
          </div>
          <Reveal delay={0.2} className="lg:col-span-4">
            <Link to="/impact" className="btn-outline-light" data-testid="impact-cta">
              Our Approach to Impact <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* LATEST INSIGHTS */}
      <section className="py-24 lg:py-32" data-testid="insights-section">
        <div className="container-rcs">
          <Section eyebrow="News & Insights" title="Latest from RCS." />
          <div className="mt-14">
            {articles.length === 0 ? (
              <Empty
                icon={Newspaper}
                title="Insights are on their way"
                text="Company news, education perspectives and announcements from Roy Career Solutions will be published here soon."
                testid="insights-empty"
              />
            ) : (
              <div className="grid gap-8 md:grid-cols-3">
                {articles.map((a, i) => (
                  <Reveal key={a.id} delay={i * 0.1}>
                    <Link to={`/news/${a.slug}`} className="card-premium block h-full" data-testid={`article-card-${a.slug}`}>
                      {a.image && <img src={fileUrl(a.image)} alt={a.title} loading="lazy" className="mb-6 aspect-[16/9] w-full rounded-xl object-cover" />}
                      <span className="eyebrow">{a.category}</span>
                      <h3 className="mt-3 font-serif text-xl text-navy-900">{a.title}</h3>
                      <p className="mt-3 text-sm leading-relaxed text-slate-600">{a.excerpt}</p>
                    </Link>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CAREERS CTA */}
      <section className="border-y border-navy-900/10 bg-white py-20" data-testid="careers-band">
        <div className="container-rcs flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
          <Reveal>
            <p className="eyebrow">Careers at RCS</p>
            <h2 className="mt-4 max-w-xl font-serif text-3xl text-navy-900 sm:text-4xl">Build the future of learning with us.</h2>
            <p className="mt-4 max-w-xl text-base text-slate-600">
              Join a team building education brands that combine learning, technology and creativity.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/careers" className="btn-gold" data-testid="careers-band-cta">
              View Careers <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CORPORATE CTA */}
      <section className="relative overflow-hidden bg-navy-950 py-28 lg:py-36" data-testid="corporate-cta">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.1]"
          style={GRID_BG}
        />
        <div className="container-rcs relative">
          <Reveal>
            <h2 className="max-w-4xl font-serif text-4xl leading-[1.1] text-ivory sm:text-5xl lg:text-6xl">
              Creating Possibilities <br className="hidden sm:block" />Through <span className="italic text-gold">Education.</span>
            </h2>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              From academic learning to music education, Roy Career Solutions Private Limited is building
              specialised platforms designed around the aspirations of modern learners.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/brands" className="btn-gold" data-testid="cta-explore-brands">
                Explore Our Brands <ArrowUpRight className="h-4 w-4" aria-hidden />
              </Link>
              <Link to="/contact" className="btn-outline-light" data-testid="cta-connect">
                Connect With RCS <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
